import {
  CONVERSATION_ACCEPT_SCHEDULE_TYPE,
  CONVERSATION_ADD_SUBMESSAGE_SUCCESS_TYPE,
  CONVERSATION_ADD_SUBMESSAGE_TYPE,
  CONVERSATION_CANCEL_SCHEDULE_TYPE,
  CONVERSATION_EDIT_SCHEDULE_SUCCESS_TYPE,
  CONVERSATION_EDIT_SCHEDULE_TYPE,
  CONVERSATION_GET_FAIL_TYPE,
  CONVERSATION_GET_SUCCESS_TYPE,
  CONVERSATION_GET_TYPE,
  CONVERSATION_MUTATE_SUCCESS_TYPE,
  CONVERSATION_SAVE_INPUT_TYPE,
  CONVERSATION_UNDO_ACCEPT_SCHEDULE_TYPE,
  CONVERSATION_UNDO_CANCEL_SCHEDULE_TYPE,
  CONVERSATIONS_UPDATE_TYPE,
  DefinedConversationsActions,
  SET_CONVERSATION_READ_STATUS_ERROR_TYPE,
  SET_CONVERSATION_READ_STATUS_TYPE,
} from './conversations.actions';
import {
  createConversationEventDocumentForNewSchedule,
  createSubmessageDocumentForCancelSchedule,
  createSubmessageDocumentForNewMessage,
  createSubmessageDocumentForNewSchedule,
  findConversationEventById,
  findConversationStateIndexByConversationId,
  updateConversationParticipant,
} from './conversations.helper';
import {
  Conversation,
  ConversationEvent,
  ConversationEventDate,
  ConversationState,
  Submessage,
} from '@libs/src/models/conversation.interfaces';

import castArray from 'lodash-es/castArray';
import cloneDeep from 'lodash-es/cloneDeep';
import compact from 'lodash-es/compact';
import find from 'lodash-es/find';
import findIndex from 'lodash-es/findIndex';
import forEach from 'lodash-es/forEach';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import merge from 'lodash-es/merge';

const DEFAULT_STATE: ConversationState[] = [];

export function conversationsReducer(
  state: ConversationState[] = DEFAULT_STATE,
  action: DefinedConversationsActions,
) {
  switch (action.type) {
    case CONVERSATIONS_UPDATE_TYPE: {
      const stateClone = cloneDeep(state);
      forEach(castArray(action.payload), (conversation: Conversation) => {
        const updateConversationIndex =
          findConversationStateIndexByConversationId(
            stateClone,
            conversation._id,
          );
        if (0 <= updateConversationIndex) {
          stateClone[updateConversationIndex] = {
            ...stateClone[updateConversationIndex],
            document: {
              ...stateClone[updateConversationIndex].document,
              ...conversation,
            },
          };
        }
      });
      return [...stateClone];
    }

    case CONVERSATION_GET_TYPE: {
      const stateClone = cloneDeep(state);
      const index = findConversationStateIndexByConversationId(
        stateClone,
        action.payload,
      );
      if (0 > index) {
        stateClone.push({
          document: { _id: action.payload },
          error: undefined,
          isLoaded: false,
          message: '',
        });
      } else {
        stateClone[index] = {
          ...stateClone[index],
          isLoaded: !isEmpty(stateClone[index].document.submessages),
        };
      }
      return [...stateClone];
    }

    case CONVERSATION_GET_SUCCESS_TYPE: {
      const stateClone = cloneDeep(state);
      const index = findConversationStateIndexByConversationId(
        stateClone,
        action.payload?._id,
      );
      if (0 <= index) {
        stateClone[index] = {
          document: {
            ...stateClone[index].document,
            ...action.payload,
          },
          error: undefined,
          isLoaded: true,
          message: stateClone[index].message || '',
        };
      } else {
        stateClone.push({
          document: action.payload,
          error: undefined,
          isLoaded: true,
          message: '',
        });
      }
      return [...stateClone];
    }

    case CONVERSATION_GET_FAIL_TYPE: {
      const stateClone = cloneDeep(state);
      const failedConversationGetIndex =
        findConversationStateIndexByConversationId(
          stateClone,
          action.payload.conversationId,
        );
      if (0 <= failedConversationGetIndex) {
        stateClone[failedConversationGetIndex] = {
          ...stateClone[failedConversationGetIndex],
          error: action.payload.error,
          isLoaded: true,
        };
      } else {
        stateClone.push({
          document: { _id: action.payload.conversationId },
          error: action.payload.error,
          isLoaded: true,
        });
      }
      return [...stateClone];
    }

    case CONVERSATION_MUTATE_SUCCESS_TYPE: {
      const stateClone = cloneDeep(state);
      const conversationMutateIndex =
        findConversationStateIndexByConversationId(
          stateClone,
          action.payload?._id,
        );
      if (0 <= conversationMutateIndex) {
        stateClone[conversationMutateIndex] = {
          ...stateClone[conversationMutateIndex],
          document: {
            ...stateClone[conversationMutateIndex].document,
            ...action.payload,
            events: merge(
              stateClone[conversationMutateIndex].document.events,
              action.payload.events,
            ),
            mostRecentMatch:
              stateClone[conversationMutateIndex].document.mostRecentMatch,
            submessages: merge(
              stateClone[conversationMutateIndex].document.submessages,
              action.payload.submessages,
            ),
          },
        };
      }
      return [...stateClone];
    }

    case SET_CONVERSATION_READ_STATUS_ERROR_TYPE:
    case SET_CONVERSATION_READ_STATUS_TYPE: {
      const stateClone = cloneDeep(state);
      const conversationToSetAsReadIndex =
        findConversationStateIndexByConversationId(
          stateClone,
          action.payload.conversation?._id,
        );
      if (0 <= conversationToSetAsReadIndex) {
        stateClone[conversationToSetAsReadIndex] = {
          ...stateClone[conversationToSetAsReadIndex],
          document: {
            ...stateClone[conversationToSetAsReadIndex].document,
            participants: updateConversationParticipant(
              stateClone[conversationToSetAsReadIndex].document.participants,
              action.payload.userId,
              { read: action.payload.read },
            ),
          },
        };
      }
      return [...stateClone];
    }

    case CONVERSATION_ACCEPT_SCHEDULE_TYPE: {
      const stateClone = cloneDeep(state);
      const chosenTime = find(
        action.payload.event.dates,
        (date: ConversationEventDate) =>
          isEqual(date?._id, action.payload.dateId),
      );
      const event = {
        ...action.payload.event,
        chosen_time: chosenTime,
        status: 'closed',
      };
      const conversationId =
        action.payload?.conversation?._id || action.payload?._id;
      const conversationIndex = findConversationStateIndexByConversationId(
        stateClone,
        conversationId,
      );
      if (0 <= conversationIndex) {
        const events = editConversationEventSchedule(
          stateClone[conversationIndex].document.events ?? [],
          event,
        );
        stateClone[conversationIndex] = {
          ...stateClone[conversationIndex],
          document: {
            ...stateClone[conversationIndex].document,
            events,
            schedule: {
              ...action.payload.event,
              ...chosenTime,
              status: 'accepted',
            },
          },
        };
      }
      return [...stateClone];
    }

    case CONVERSATION_UNDO_ACCEPT_SCHEDULE_TYPE: {
      const stateClone = cloneDeep(state);
      const conversationIndexToUndoAcceptEvent =
        findConversationStateIndexByConversationId(
          stateClone,
          action.payload?.conversation._id,
        );
      if (0 <= conversationIndexToUndoAcceptEvent) {
        const updatedEventsWithUndoneAcceptedEvent =
          editConversationEventSchedule(
            stateClone[conversationIndexToUndoAcceptEvent].document.events ??
              [],
            action.payload.event,
          );
        stateClone[conversationIndexToUndoAcceptEvent] = {
          ...stateClone[conversationIndexToUndoAcceptEvent],
          document: {
            ...stateClone[conversationIndexToUndoAcceptEvent].document,
            events: updatedEventsWithUndoneAcceptedEvent,
          },
        };
      }
      return [...stateClone];
    }

    case CONVERSATION_EDIT_SCHEDULE_TYPE: {
      const stateClone = cloneDeep(state);
      const conversationIndex = findConversationStateIndexByConversationId(
        stateClone,
        action.payload?.conversation._id,
      );

      if (0 <= conversationIndex) {
        const conversation = stateClone[conversationIndex].document;
        const updatedEvents = editConversationEventSchedule(
          conversation.events ?? [],
          action.payload.event,
        );
        const editedEventId = String(action.payload.event._id);
        const submessages = conversation.submessages ?? [];
        const updatedSubmessages = submessages.map((submessage: Submessage) => {
          if (submessage.event && String(submessage.event) === editedEventId) {
            return {
              ...submessage,
              author: action.payload.user._id,
              date: new Date().toISOString(),
            };
          }
          return submessage;
        });
        const reorderedSubmessages = [
          ...updatedSubmessages.filter(
            (submessage: Submessage) =>
              !(submessage.event && String(submessage.event) === editedEventId),
          ),
          ...updatedSubmessages.filter(
            (submessage: Submessage) =>
              submessage.event && String(submessage.event) === editedEventId,
          ),
        ];
        stateClone[conversationIndex] = {
          ...stateClone[conversationIndex],
          document: {
            ...stateClone[conversationIndex].document,
            events: updatedEvents,
            submessages: reorderedSubmessages,
          },
        };
      }
      return [...stateClone];
    }

    case CONVERSATION_EDIT_SCHEDULE_SUCCESS_TYPE: {
      const stateClone = cloneDeep(state);
      const conversationId =
        action.payload?.conversation?._id || action.payload?._id;
      const currentConversationIndex =
        findConversationStateIndexByConversationId(stateClone, conversationId);
      if (0 <= currentConversationIndex) {
        const submessageIndex = findSubmessageIndexByEventId(
          stateClone[currentConversationIndex].document.submessages,
          action.payload?.event._id,
        );
        const updatedEvents = editConversationEventSchedule(
          stateClone[currentConversationIndex].document.events ?? [],
          action.payload.event,
        );
        const updatedSubmessages = stateClone[
          currentConversationIndex
        ].document.submessages?.map((submessage: Submessage) => {
          if (
            submessage.event &&
            findSubmessageIndexByEventId(
              stateClone[currentConversationIndex].document.submessages,
              submessage.event,
            ) !== submessageIndex
          ) {
            return submessage;
          }
          return action.payload.submessage;
        });
        stateClone[currentConversationIndex] = {
          ...stateClone[currentConversationIndex],
          document: {
            ...stateClone[currentConversationIndex].document,
            events: updatedEvents,
            submessages: updatedSubmessages,
          },
        };
      }
      return [...stateClone];
    }

    case CONVERSATION_ADD_SUBMESSAGE_TYPE: {
      const stateClone = cloneDeep(state);
      const conversationIndex = findConversationStateIndexByConversationId(
        stateClone,
        action.payload?.conversation._id,
      );
      if (0 <= conversationIndex) {
        const newSubmessages = compact([
          action.payload.schedule
            ? createSubmessageDocumentForNewSchedule(
                action.payload.user,
                action.payload.schedule,
              )
            : null,
          action.payload.message
            ? createSubmessageDocumentForNewMessage(
                action.payload.user,
                action.payload.message,
              )
            : null,
        ]);
        const newEvent = action.payload.schedule
          ? [
              createConversationEventDocumentForNewSchedule(
                action.payload.user,
                action.payload.schedule,
              ),
            ]
          : [];
        stateClone[conversationIndex] = {
          ...stateClone[conversationIndex],
          document: {
            ...stateClone[conversationIndex].document,
            events: [
              ...(stateClone[conversationIndex].document.events ?? []),
              ...newEvent,
            ],
            submessages: [
              ...(stateClone[conversationIndex].document.submessages ?? []),
              ...newSubmessages,
            ],
          },
        };
      }
      return [...stateClone];
    }

    case CONVERSATION_ADD_SUBMESSAGE_SUCCESS_TYPE: {
      const stateClone = cloneDeep(state);
      const conversationIndex = findConversationStateIndexByConversationId(
        stateClone,
        action.payload._id,
      );

      if (0 <= conversationIndex) {
        stateClone[conversationIndex] = {
          ...stateClone[conversationIndex],
          document: {
            ...stateClone[conversationIndex].document,
            ...action.payload,
          },
        };
      }

      return [...stateClone];
    }

    case CONVERSATION_CANCEL_SCHEDULE_TYPE: {
      const stateClone = cloneDeep(state);
      const conversationIndexToCancelSchedule =
        findConversationStateIndexByConversationId(
          stateClone,
          action.payload?.conversation._id,
        );
      if (0 > conversationIndexToCancelSchedule) {
        return [...stateClone];
      }
      const conversation =
        stateClone[conversationIndexToCancelSchedule].document;
      const eventToCancelIndex = findConversationEventById(
        conversation.events,
        action.payload?.event._id,
      );
      if (0 <= eventToCancelIndex) {
        const updatedEvents = [...conversation.events];
        updatedEvents[eventToCancelIndex] = {
          ...updatedEvents[eventToCancelIndex],
          status: 'cancelled',
        };
        const cancelSubmessage = createSubmessageDocumentForCancelSchedule(
          action.payload.user,
          updatedEvents[eventToCancelIndex],
        );
        const updatedSubmessages = [
          ...conversation.submessages.map((submessage) =>
            submessage.event === action.payload?.event._id
              ? { ...submessage, status: 'cancelled' }
              : submessage,
          ),
          cancelSubmessage,
        ];
        stateClone[conversationIndexToCancelSchedule] = {
          ...stateClone[conversationIndexToCancelSchedule],
          document: {
            ...conversation,
            events: updatedEvents,
            submessages: updatedSubmessages,
          },
        };
      }
      return [...stateClone];
    }

    case CONVERSATION_UNDO_CANCEL_SCHEDULE_TYPE: {
      const stateClone = cloneDeep(state);
      const conversationIndexToUndoCancelSchedule =
        findConversationStateIndexByConversationId(
          stateClone,
          action.payload?.conversation._id,
        );
      if (0 > conversationIndexToUndoCancelSchedule) {
        return [...stateClone];
      }
      const { document } = stateClone[conversationIndexToUndoCancelSchedule];
      const eventToUndoCancelIndex = findConversationEventById(
        document.events,
        action.payload?.event._id,
      );
      if (0 > eventToUndoCancelIndex) {
        return [...stateClone];
      }
      document.events[eventToUndoCancelIndex].status =
        action.payload.event.status;
      document.submessages =
        document.submessages?.filter(
          (submessage: Submessage) => submessage._id,
        ) ?? [];
      return [...stateClone];
    }

    case CONVERSATION_SAVE_INPUT_TYPE: {
      const stateClone = cloneDeep(state);
      const index = findConversationStateIndexByConversationId(
        stateClone,
        action.payload.conversationId,
      );
      stateClone[index] = {
        ...stateClone[index],
        message: action.payload.message,
      };
      return stateClone;
    }

    default: {
      return state;
    }
  }
}

function editConversationEventSchedule(
  conversationEvents: ConversationEvent[],
  eventUpdate: ConversationEvent,
) {
  const events = [...conversationEvents];
  const eventIndex = findIndex(events, (conversationEvent: ConversationEvent) =>
    isEqual(conversationEvent._id, eventUpdate._id),
  );
  events[eventIndex] = {
    ...events[eventIndex],
    ...eventUpdate,
  };
  return events;
}

function findSubmessageIndexByEventId(
  submessages: Submessage[] | undefined,
  eventId: string,
) {
  return findIndex(
    submessages,
    (submessage: Submessage) => eventId === submessage?.event,
  );
}
